import React, { memo, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { queryClient } from 'src/query-client';
import { Checkbox, Spin } from 'antd';
import { InfiniteScroll } from '@components/InfiniteScroll/InfiniteScroll';
import { useCurrentWorkspace } from '@hooks/workspace';
import useAssetSelection from '@hooks/useAssetSelection';
import useMarqueeSelection from '@hooks/useMarqueeSelection';
import AssetsEmptyState from '@components/Assets/AssetsEmptyState';
import { moveAssetToFolder } from '@redux/actions/assetsAction';
import { moveAsset } from '@api/Asset';
import { AssetsList, FoldersList } from '@components/Assets';
import BulkActions from '@components/Assets/BulkActions';
import {
  toggleCampaignFolderCreationModal,
  toggleMoveAssetToPrivateFolder,
  toggleUploadAssetModal
} from '@redux/actions/modalAction';
import { useLayout } from '@layouts/PageLayout';
import { useDebounced } from '@helpers/useDebounced';
import { applyMarqueeAssetSelection } from '@helpers/applyMarqueeAssetSelection';
import { useAssetsQuery, useAssetsCountQuery } from '@hooks/queries/assets';
import { AssetItemDto, CampaignItemDto } from '@api/Api';
import { optimisticListItemRemove } from '@hooks/queries/helpers';
import { PlusIcon } from '@icons';
import AssetsTabControls, {
  getInitialAssetsOrderBy
} from '@pages/Campaigns/CampaignItem/components/AssetsTab/AssetsTabControls';

interface IAssetsTabProps {
  campaign: CampaignItemDto;
  isActive: boolean;
}

const assetsOrderParams = [
  'createdAt:ASC',
  'createdAt:DESC',
  'modifiedAt:ASC',
  'modifiedAt:DESC',
  'deletedAt:ASC',
  'deletedAt:DESC',
  'openedAt:ASC',
  'openedAt:DESC',
  'order:DESC',
  'name:ASC',
  'name:DESC'
] as const;

export default memo(function AssetsTab({
  campaign,
  isActive
}: IAssetsTabProps) {
  const [searchParams] = useSearchParams();
  const { currentView } = useLayout();
  const dispatch = useDispatch();
  const [workspace] = useCurrentWorkspace();
  const [searchQuery, setSearchQuery] = useState('');
  const [orderBy, setOrderBy] = useState(getInitialAssetsOrderBy);

  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  const isTileView = currentView === 'tile';

  const orderByParam: (typeof assetsOrderParams)[number][] | undefined =
    useMemo(
      () =>
        orderBy && assetsOrderParams.includes(orderBy as any)
          ? [orderBy as (typeof assetsOrderParams)[number]]
          : undefined,
      [orderBy]
    );
  const debouncedSearchQuery = useDebounced(searchQuery, 250);

  const prevCampaignId = useRef(campaign.id);

  const flatten = !!debouncedSearchQuery;
  const assetsQuery = useAssetsQuery(
    {
      workspaceId: campaign.workspaceId as any,
      campaignId: campaign.id as any,
      flatten,
      isFolder: false,
      searchQuery: debouncedSearchQuery || undefined,
      orderBy: orderByParam
    },
    {
      enabled: isActive,
      keepPreviousData: campaign.id === prevCampaignId.current
    }
  );
  const assetsCountQuery = useAssetsCountQuery(
    {
      workspaceId: campaign.workspaceId as any,
      campaignId: campaign.id as any,
      flatten,
      isFolder: false,
      searchQuery: debouncedSearchQuery || undefined
    },
    {
      enabled: isActive,
      keepPreviousData: campaign.id === prevCampaignId.current,
      placeholderData: campaign.assetsCount
    }
  );

  const foldersQuery = useAssetsQuery(
    {
      workspaceId: campaign.workspaceId as any,
      campaignId: campaign.id as any,
      flatten,
      isFolder: true,
      searchQuery: debouncedSearchQuery || undefined,
      orderBy: orderByParam
    },
    {
      enabled: isActive,
      keepPreviousData: campaign.id === prevCampaignId.current
    }
  );
  const foldersCountQuery = useAssetsCountQuery(
    {
      workspaceId: campaign.workspaceId as any,
      campaignId: campaign.id as any,
      flatten,
      isFolder: true,
      searchQuery: debouncedSearchQuery || undefined
    },
    {
      enabled: isActive,
      keepPreviousData: campaign.id === prevCampaignId.current,
      placeholderData: campaign.foldersCount
    }
  );

  if (campaign.id !== prevCampaignId.current) {
    prevCampaignId.current = campaign.id;
  }

  const assetsList = useMemo(() => {
    return assetsQuery.data?.pages.flatMap((page) => page.edges) || [];
  }, [assetsQuery.data]);

  const foldersList = useMemo(() => {
    return foldersQuery.data?.pages.flatMap((page) => page.edges) || [];
  }, [foldersQuery.data]);

  const assetsCount = assetsCountQuery.data ?? 0;
  const foldersCount = foldersCountQuery.data ?? 0;
  const assetsToFetch = Math.max(0, assetsCount - assetsList.length);
  const foldersToFetch = Math.max(0, foldersCount - foldersList.length);

  const {
    selected: selectedFolders,
    setSelected: setSelectedFolders,
    loading: loadingFolders,
    onSelectAll: onSelectAllFolders,
    setSelectedItems: setSelectedFoldersItems
  } = useAssetSelection(foldersQuery);
  const {
    selected: selectedAssets,
    setSelected: setSelectedAssets,
    loading: loadingAssets,
    onSelectAll: onSelectAllAssets,
    setSelectedItems: setSelectedAssetsItems
  } = useAssetSelection(assetsQuery);

  const marqueeContainerRef = useRef<HTMLDivElement>(null);
  const foldersListRef = useRef(foldersList);
  foldersListRef.current = foldersList;
  const assetsListRef = useRef(assetsList);
  assetsListRef.current = assetsList;

  const { marqueeBox } = useMarqueeSelection({
    containerRef: marqueeContainerRef,
    enabled: isActive,
    onSelect: (items) =>
      applyMarqueeAssetSelection({
        items,
        foldersList: foldersListRef.current,
        assetsList: assetsListRef.current,
        setSelectedFolders,
        setSelectedAssets
      })
  });

  const allowUpload = !!campaign.permissions.createAssets && !campaign.demo;
  const showEmptyState = allowUpload || !campaign.foldersCount;

  return (
    <>
      <AssetsTabControls
        searchQuery={searchQuery}
        orderBy={orderBy}
        onSearchChange={setSearchQuery}
        onOrderByChange={setOrderBy}
      />
      <div
        ref={marqueeContainerRef}
        className="marquee-selection-container"
        style={{
          paddingRight: 34,
          paddingLeft: 34,
          marginRight: -34,
          marginLeft: -34,
          height: 'calc(100vh - 340px - var(--demo-mode-panel-width, 0px))',
          overflow: 'auto'
        }}
      >
        {marqueeBox}
        <div className="campaign_items">
          <div className="campaign_items__top">
            <button
              type="button"
              className="campaign_items__title"
              disabled={!allowUpload}
              onClick={() => {
                if (!allowUpload) return;
                dispatch(
                  toggleCampaignFolderCreationModal({
                    entity: 'folder',
                    campaignId: campaign.id,
                    visible: true
                  })
                );
              }}
            >
              {campaign.foldersCount ? 'Folders' : 'Add folder'}{' '}
              {allowUpload && <PlusIcon />}
            </button>
            {!!foldersList?.length && isTileView && (
              <Checkbox
                disabled={loadingFolders}
                checked={
                  !!selectedFolders.length &&
                  selectedFolders.length === foldersCount
                }
                indeterminate={
                  !!selectedFolders.length &&
                  selectedFolders.length < foldersCount
                }
                onChange={onSelectAllFolders}
              >
                Select all
              </Checkbox>
            )}
          </div>

          <Spin spinning={loadingFolders}>
            <InfiniteScroll
              loading={
                !loadingFolders && foldersQuery.isFetchingNextPage
                  ? 'more'
                  : false
              }
              next={foldersQuery.fetchNextPage}
              hasMore={foldersQuery.hasNextPage ?? false}
            >
              <FoldersList
                canShare={campaign.permissions.createReviews}
                selectedFolders={selectedFolders}
                foldersCount={foldersCount}
                setSelectedFolders={setSelectedFoldersItems}
                foldersList={foldersList}
                countToFetch={
                  !loadingFolders &&
                  (foldersQuery.isLoading || foldersQuery.isFetchingNextPage)
                    ? foldersToFetch
                    : 0
                }
                isCardViewMode={isTileView}
                canReorganize={orderBy === 'order:DESC'}
                canDropInside={true}
                listLoading={foldersQuery.isLoading}
                onAssetDrop={(assetId, folderId) => {
                  const asset = assetsList
                    .map((x) => x.node)
                    .find((x) => x.id === assetId);
                  const folder = foldersList
                    .map((x) => x.node)
                    .find((x) => x.id === folderId);
                  if (!asset?.asset || !folder?.folder) return;
                  const { permissions } = asset.asset;
                  const canMove =
                    (folder.isPrivate && permissions.moveToPrivateFolder) ||
                    (!folder.isPrivate && permissions.moveToPublicFolder);
                  if (!canMove) return;
                  const version = asset.asset.versions[0];
                  const onConfirm = () =>
                    dispatch(moveAssetToFolder({ assetId, folderId }));
                  if (folder.isPrivate) {
                    dispatch(
                      toggleMoveAssetToPrivateFolder({
                        visible: true,
                        folderName: folder.folder.name,
                        assetsName: [`${version.name}.${version.extension}`],
                        callback: onConfirm
                      })
                    );
                  } else onConfirm();
                }}
                onFolderDrop={async (sourceFolderId, targetFolderId) => {
                  if (sourceFolderId === targetFolderId) return;
                  const sourceFolder = foldersList
                    .map((x) => x.node)
                    .find((x) => x.id === sourceFolderId);
                  const targetFolder = foldersList
                    .map((x) => x.node)
                    .find((x) => x.id === targetFolderId);
                  if (!sourceFolder?.folder || !targetFolder?.folder) return;
                  const { permissions } = sourceFolder.folder;
                  const canMove = targetFolder.isPrivate
                    ? permissions.moveToPrivateFolder
                    : permissions.moveToPublicFolder;
                  if (!canMove) return;
                  const onConfirm = async () => {
                    await queryClient.cancelQueries({ queryKey: ['assets'] });
                    optimisticListItemRemove<AssetItemDto>({
                      queryKey: ['assets', 'list'],
                      id: sourceFolderId
                    });
                    await moveAsset({
                      assetId: sourceFolderId,
                      folderId: targetFolderId
                    });
                    await queryClient.invalidateQueries({
                      queryKey: ['assets']
                    });
                  };
                  if (!sourceFolder.isPrivate && targetFolder.isPrivate) {
                    dispatch(
                      toggleMoveAssetToPrivateFolder({
                        visible: true,
                        folderName: targetFolder.folder.name,
                        assetsName: [sourceFolder.folder.name],
                        callback: onConfirm
                      })
                    );
                  } else {
                    await onConfirm();
                  }
                }}
              />
            </InfiniteScroll>
          </Spin>

          {assetsCount ? (
            <Spin spinning={loadingAssets}>
              <InfiniteScroll
                loading={
                  !loadingAssets && assetsQuery.isFetchingNextPage
                    ? 'more'
                    : false
                }
                next={assetsQuery.fetchNextPage}
                hasMore={assetsQuery.hasNextPage ?? false}
              >
                <div className="campaign_items__top">
                  <button
                    type="button"
                    className="campaign_items__title"
                    onClick={() => {
                      if (!allowUpload) return;
                      dispatch(
                        toggleUploadAssetModal({
                          visible: true,
                          workspaceId: workspace.id,
                          campaignId: campaign.id
                        })
                      );
                    }}
                  >
                    Media {allowUpload && <PlusIcon />}
                  </button>
                  {!!assetsList?.length && isTileView && (
                    <Checkbox
                      disabled={loadingAssets}
                      checked={
                        !!selectedAssets.length &&
                        selectedAssets.length === assetsCount
                      }
                      indeterminate={
                        !!selectedAssets.length &&
                        selectedAssets.length < assetsCount
                      }
                      onChange={onSelectAllAssets}
                    >
                      Select all
                    </Checkbox>
                  )}
                </div>

                <AssetsList
                  campaign={campaign}
                  searchQuery={searchQuery || null}
                  selectedAssets={selectedAssets}
                  setSelectedAssets={setSelectedAssetsItems}
                  assetsList={assetsList}
                  displayAssetsCount={assetsCount}
                  isTileView={isTileView}
                  page="campaign"
                  countToFetch={
                    !loadingAssets && assetsQuery.isFetchingNextPage
                      ? assetsToFetch
                      : 0
                  }
                  canReorganize={orderBy === 'order:DESC'}
                  canCombine={true}
                  listLoading={assetsQuery.isLoading}
                  canShare={campaign.permissions.createReviews}
                  isShowCompany={false}
                />
              </InfiniteScroll>
            </Spin>
          ) : (
            showEmptyState && (
              <AssetsEmptyState
                campaign={campaign}
                isSearchEmptyState={false}
              />
            )
          )}
          <BulkActions
            selectedAssets={selectedAssets}
            selectedFolders={selectedFolders}
            canShare={campaign.permissions.createReviews}
            page="campaign"
            onRefreshAssetList={assetsQuery.refetch}
            onClose={() => {
              setSelectedAssets([]);
              setSelectedFolders([]);
            }}
          />
        </div>
      </div>
    </>
  );
});
